import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { AuthContextType, LoginCredentials, RegisterCredentials } from '../types';
import { authApi, handleApiError } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const initializeAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
        try {
          // 验证token有效性
          await authApi.validateToken();
      setIsAuthenticated(true);
        } catch (error) {
          // Token无效，清除本地存储
          console.warn('Token validation failed:', error);
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        }
    }
    setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      const result = await authApi.login(credentials);

      if (result.token) {
        localStorage.setItem('token', result.token);
        setIsAuthenticated(true);
        toast.success('登录成功！欢迎回来');
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (credentials: RegisterCredentials): Promise<void> => {
    try {
      setIsLoading(true);
      await authApi.register(credentials);
      toast.success('注册成功！请登录');
    } catch (error) {
      const message = handleApiError(error);
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = (): void => {
    authApi.logout();
    setIsAuthenticated(false);
    toast.success('已退出登录');
  };

  // 使用已有token直接登录（用于验证码登录等场景）
  const loginWithToken = (token: string): void => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    loginWithToken,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 